/**
 * Color configuration types and standard values for the FloatingToolbar component.
 * Maps MD3 design tokens to CSS custom properties used in Tailwind CSS.
 */

/**
 * Color configuration types and standard values for the FloatingToolbar component.
 * Maps MD3 design tokens to CSS custom properties used in Tailwind CSS.
 */

export type ToolbarVariant =
	| "standard"
	| "vibrant"
	| "surface-high"
	| "surface-highest"
	| "tertiary"
	| "xr";

export interface FloatingToolbarColors {
	/** The background color of the toolbar container */
	toolbarContainerColor?: string;
	/** The color of the content (icons, text) inside the toolbar */
	toolbarContentColor?: string;
	/** The background color of the FAB container */
	fabContainerColor?: string;
	/** The color of the content inside the FAB */
	fabContentColor?: string;
}

/**
 * Standard color configuration for the floating toolbar.
 * Uses surface container for the toolbar and secondary container for the FAB.
 */
export const standardFloatingToolbarColors: FloatingToolbarColors = {
	toolbarContainerColor: "var(--md-sys-color-surface-container)",
	toolbarContentColor: "var(--md-sys-color-on-surface)",
	fabContainerColor: "var(--md-sys-color-secondary-container)",
	fabContentColor: "var(--md-sys-color-on-secondary-container)",
};

/**
 * Vibrant color configuration for the floating toolbar.
 * Uses primary container for the toolbar and tertiary container for the FAB.
 */
export const vibrantFloatingToolbarColors: FloatingToolbarColors = {
	toolbarContainerColor: "var(--md-sys-color-primary-container)",
	toolbarContentColor: "var(--md-sys-color-on-primary-container)",
	fabContainerColor: "var(--md-sys-color-tertiary-container)",
	fabContentColor: "var(--md-sys-color-on-tertiary-container)",
};

/**
 * Surface Container High color configuration for the toolbar (XR / Elevated).
 */
export const surfaceContainerHighFloatingToolbarColors: FloatingToolbarColors =
	{
		toolbarContainerColor: "var(--md-sys-color-surface-container-high)",
		toolbarContentColor: "var(--md-sys-color-on-surface)",
		fabContainerColor: "var(--md-sys-color-secondary-container)",
		fabContentColor: "var(--md-sys-color-on-secondary-container)",
	};

/**
 * Surface Container Highest color configuration for the toolbar (XR / Elevated).
 */
export const surfaceContainerHighestFloatingToolbarColors: FloatingToolbarColors =
	{
		toolbarContainerColor: "var(--md-sys-color-surface-container-highest)",
		toolbarContentColor: "var(--md-sys-color-on-surface)",
		fabContainerColor: "var(--md-sys-color-secondary-container)",
		fabContentColor: "var(--md-sys-color-on-secondary-container)",
	};

/**
 * Tertiary Container color configuration for the toolbar.
 */
export const tertiaryContainerFloatingToolbarColors: FloatingToolbarColors = {
	toolbarContainerColor: "var(--md-sys-color-tertiary-container)",
	toolbarContentColor: "var(--md-sys-color-on-tertiary-container)",
	fabContainerColor: "var(--md-sys-color-primary-container)",
	fabContentColor: "var(--md-sys-color-on-primary-container)",
};

/**
 * XR Glassmorphic color configuration for the toolbar.
 * Uses semi-transparent surface container high with backdrop blur.
 */
export const xrFloatingToolbarColors: FloatingToolbarColors = {
	toolbarContainerColor:
		"color-mix(in srgb, var(--md-sys-color-surface-container-high) 80%, transparent)",
	toolbarContentColor: "var(--md-sys-color-on-surface)",
	fabContainerColor:
		"color-mix(in srgb, var(--md-sys-color-secondary-container) 85%, transparent)",
	fabContentColor: "var(--md-sys-color-on-secondary-container)",
};

/**
 * Helper function to resolve FloatingToolbarColors based on variant prop or custom override.
 */
export function getToolbarColors(
	variant: ToolbarVariant = "standard",
	customColors?: FloatingToolbarColors,
): FloatingToolbarColors {
	if (customColors) return customColors;

	switch (variant) {
		case "vibrant":
			return vibrantFloatingToolbarColors;
		case "surface-high":
			return surfaceContainerHighFloatingToolbarColors;
		case "surface-highest":
			return surfaceContainerHighestFloatingToolbarColors;
		case "tertiary":
			return tertiaryContainerFloatingToolbarColors;
		case "xr":
			return xrFloatingToolbarColors;
		default:
			return standardFloatingToolbarColors;
	}
}
